/**
 * Dashboard — Zentrale Übersicht mit Grid-Layout
 * DESIGN: Nordic Command Center, Koop Red (#C41E3A), Plus Jakarta Sans
 * Widgets: Stats, Kalender (Tag/Woche/Monat), Akten, Aufgaben, Team, Schnellzugriff
 * Google Calendar: OAuth-Flow, bidirektionale Sync, Token-Handling
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { BETRIEBE, BETRIEB_LIST, type BetriebId } from '@/lib/betriebe';
import { STATUS_LABELS, STATUS_COLORS, PRIORITAET_COLORS, supabase, type BusinessUnit } from '@/lib/supabase';
import { useAkten, useAufgaben, useAktenCounts, useTermine, useMitarbeiter } from '@/hooks/useSupabase';
import { useGoogleCalendar, type GoogleCalendarEvent } from '@/hooks/useGoogleCalendar';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'wouter';
import {
  FolderOpen, Clock, FileText, BarChart3, TrendingUp, Package,
  Globe, Home, Wrench, Scale, CalendarDays, CheckSquare, Users,
  ChevronLeft, ChevronRight, Plus, Bell, RefreshCw,
  Filter, ChevronDown, Link2, Unlink, ExternalLink, X, MapPin, AlignLeft, UtensilsCrossed, Car,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { filterKiAgent } from '@/lib/kiAgent';
import VerpflegungWidget from '@/components/VerpflegungWidget';
import VerkaufsEmpfehlungenWidget from '@/components/VerkaufsEmpfehlungenWidget';
import FahrzeugAlertsListe from '@/components/FahrzeugAlertsListe';
import PoolFahrzeugStatusWidget from '@/components/fahrzeuge/PoolFahrzeugStatusWidget';
import FleetKPIWidget from '@/components/fahrzeuge/FleetKPIWidget';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const KOOP_RED = '#C41E3A';
const KOOP_DARK = '#9B1C1C';
const SHADOW_SM = '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)';
const SHADOW_MD = '0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.08)';
const SHADOW_GLOW = '0 4px 20px rgba(196,30,58,0.15)';

const BETRIEB_ICONS: Record<BetriebId, LucideIcon> = {
  gutachten: FileText, autoglas: BarChart3, mietwagen: TrendingUp,
  werkstatt: Wrench, protex: Package, unfallgiganten: Globe, airbnb: Home, recht: Scale,
  unfallwiki: Globe, app_programmierung: FileText,
};
// Note: BETRIEB_ICONS kept for potential future use in calendar event display

// TEAM wird aus Supabase geladen (kein hardcoded Array)

const STATUS_DOT: Record<string, string> = {
  aktiv: 'bg-emerald-500', pause: 'bg-amber-400', inaktiv: 'bg-gray-300',
};

function getInitials(vorname: string, nachname: string) {
  return `${vorname.charAt(0)}${nachname.charAt(0)}`.toUpperCase();
}

type CalendarView = 'day' | 'week' | 'month';

// Google Calendar event colors
const GCAL_COLORS: Record<string, string> = {
  '1': '#7986CB', '2': '#33B679', '3': '#8E24AA', '4': '#E67C73',
  '5': '#F6BF26', '6': '#F4511E', '7': '#039BE5', '8': '#616161',
  '9': '#3F51B5', '10': '#0B8043', '11': '#D50000',
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getWeekDates(date: Date): Date[] {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(monday);
    dd.setDate(monday.getDate() + i);
    return dd;
  });
}

function formatTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function toISOLocal(date: Date) {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// ─── WIDGET WRAPPER ───────────────────────────────────────────────────────────
function Widget({ children, className = '', title, icon: Icon, badge, action, noPadding }: {
  children: React.ReactNode; className?: string; title?: string;
  icon?: LucideIcon; badge?: number | string; action?: React.ReactNode; noPadding?: boolean;
}) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 overflow-hidden ${className}`}
      style={{ boxShadow: SHADOW_SM }}>
      {title && (
        <div className="px-5 py-3.5 border-b border-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {Icon && (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(196,30,58,0.06)' }}>
                <Icon className="w-4 h-4" style={{ color: KOOP_RED }} />
              </div>
            )}
            <h3 className="text-sm font-bold text-gray-900 tracking-tight">{title}</h3>
            {badge !== undefined && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{badge}</span>
            )}
          </div>
          {action}
        </div>
      )}
      <div className={noPadding ? '' : 'p-5'}>{children}</div>
    </div>
  );
}

// ─── NEW EVENT DIALOG ────────────────────────────────────────────────────────
function NewEventDialog({ open, onClose, onSubmit, initialDate }: {
  open: boolean; onClose: () => void;
  onSubmit: (event: { summary: string; description: string; location: string; start: string; end: string }) => void;
  initialDate?: Date;
}) {
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const now = initialDate || new Date();
      const startDefault = new Date(now);
      startDefault.setMinutes(0, 0, 0);
      startDefault.setHours(startDefault.getHours() + 1);
      const endDefault = new Date(startDefault);
      endDefault.setHours(endDefault.getHours() + 1);
      setSummary('');
      setDescription('');
      setLocation('');
      setStart(toISOLocal(startDefault));
      setEnd(toISOLocal(endDefault));
      setSaving(false);
    }
  }, [open, initialDate]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!summary.trim()) return;
    setSaving(true);
    try {
      await onSubmit({ summary, description, location, start, end });
    } catch {
      // Error handled in parent
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-full sm:h-auto sm:max-w-md sm:mx-4 rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(196,30,58,0.08)' }}>
              <Plus className="w-4 h-4" style={{ color: KOOP_RED }} />
            </div>
            <h3 className="text-sm font-bold text-gray-900">Neuer Termin</h3>
          </div>
          <button onClick={onClose} className="w-11 h-11 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Titel *</label>
            <input type="text" value={summary} onChange={e => setSummary(e.target.value)} required
              placeholder="z.B. Besichtigung VW Golf"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Start</label>
              <DateTimePicker value={start} onChange={v => setStart(v ?? '')} />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Ende</label>
              <DateTimePicker value={end} onChange={v => setEnd(v ?? '')} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Ort</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
              <input type="text" value={location} onChange={e => setLocation(e.target.value)}
                placeholder="Adresse oder Ort"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Beschreibung</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Notizen zum Termin..."
              rows={2}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all resize-none" />
          </div>
          <div className="flex gap-2 pt-1 mobile-dialog-actions sm:pb-0">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
              Abbrechen
            </button>
            <button type="submit" disabled={saving || !summary.trim() || !start || !end}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              style={{ backgroundColor: KOOP_RED, boxShadow: SHADOW_GLOW }}>
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Speichern...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CalendarDays className="w-3.5 h-3.5" /> Termin erstellen
                </span>
              )}
            </button>
          </div>
          <p className="text-[10px] text-gray-400 text-center">
            {import.meta.env.VITE_GOOGLE_CLIENT_ID ? 'Termin wird in Google Calendar gespeichert' : 'Termin wird lokal gespeichert (Google Calendar nicht verbunden)'}
          </p>
        </form>
      </div>
    </div>
  );
}

// ─── GOOGLE CALENDAR STATUS BANNER ───────────────────────────────────────────
function GoogleCalendarBanner() {
  const gcal = useGoogleCalendar();
  const clientIdConfigured = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

  if (gcal.isLoading) return null;

  // Not configured at all
  if (!clientIdConfigured) {
    return (
      <div className="bg-amber-50/60 border border-amber-100 rounded-xl px-5 py-3 flex items-center gap-3">
        <Bell className="w-4 h-4 text-amber-500 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs text-amber-700">
            <strong>Google Calendar:</strong> Noch nicht konfiguriert. Bitte <code className="text-[10px] bg-amber-100 px-1 py-0.5 rounded">VITE_GOOGLE_CLIENT_ID</code> in Vercel setzen.
          </p>
        </div>
      </div>
    );
  }

  // Configured but not connected
  if (!gcal.isConnected) {
    return (
      <div className="bg-blue-50/60 border border-blue-100 rounded-xl px-5 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
              <path d="M18 3H6C4.34 3 3 4.34 3 6v12c0 1.66 1.34 3 3 3h12c1.66 0 3-1.34 3-3V6c0-1.66-1.34-3-3-3z" fill="#4285F4"/>
              <path d="M18 3h-6v9l3-2.25L18 12V3z" fill="#EA4335"/>
              <path d="M18 3H6C4.34 3 3 4.34 3 6v12c0 1.66 1.34 3 3 3h12c1.66 0 3-1.34 3-3V6c0-1.66-1.34-3-3-3z" fill="none" stroke="#fff" strokeWidth="0.5"/>
            </svg>
          </div>
          <div>
            <p className="text-xs font-bold text-blue-800">Google Calendar verbinden</p>
            <p className="text-[10px] text-blue-600 mt-0.5">Termine von info@gutachter-koop.de synchronisieren</p>
          </div>
        </div>
        <button onClick={gcal.connect}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all hover:scale-105 active:scale-95 flex-shrink-0">
          <Link2 className="w-3.5 h-3.5" /> Verbinden
        </button>
      </div>
    );
  }

  // Connected
  return (
    <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl px-5 py-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <p className="text-xs font-bold text-emerald-800">Google Calendar verbunden</p>
          <p className="text-[10px] text-emerald-600 mt-0.5">{gcal.googleEmail}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {gcal.error && (
          <span className="text-[10px] text-red-500 font-medium">{gcal.error}</span>
        )}
        <button onClick={gcal.disconnect}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold text-red-600 border border-red-200 hover:bg-red-50 transition-all">
          <Unlink className="w-3 h-3" /> Trennen
        </button>
      </div>
    </div>
  );
}

// ─── CALENDAR WIDGET (with Google Calendar events) ───────────────────────────
export function CalendarWidget({ termine, googleEvents, onNewEvent }: {
  termine: any[]; googleEvents: GoogleCalendarEvent[]; onNewEvent: (d?: Date) => void;
}) {
  const [, setLocation] = useLocation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>(window.innerWidth < 640 ? 'day' : 'week');
  const [betriebFilter, setBetriebFilter] = useState<string>('alle');
  const [showFilter, setShowFilter] = useState(false);
  const [showGoogleEvents, setShowGoogleEvents] = useState(true);

  // Merge Supabase termine with Google Calendar events
  const allEvents = useMemo(() => {
    const supabaseEvents = (termine || [])
      .filter(t => betriebFilter === 'alle' || t.business_unit === betriebFilter)
      .map(t => ({
        id: t.id,
        title: t.titel,
        start: t.beginn,
        end: t.ende,
        source: 'supabase' as const,
        businessUnit: t.business_unit as BetriebId,
        mitarbeiter: t.mitarbeiter_name,
        color: BETRIEBE[t.business_unit as BetriebId]?.color?.accent || KOOP_RED,
        bgColor: BETRIEBE[t.business_unit as BetriebId]?.color?.bgLight || 'rgba(196,30,58,0.06)',
      }));

    const gEvents = showGoogleEvents ? (googleEvents || []).map(e => ({
      id: e.id,
      title: e.summary || '(Kein Titel)',
      start: e.start,
      end: e.end,
      source: 'google' as const,
      businessUnit: undefined as BetriebId | undefined,
      mitarbeiter: e.creator,
      color: GCAL_COLORS[e.color_id] || '#4285F4',
      bgColor: `${GCAL_COLORS[e.color_id] || '#4285F4'}15`,
      htmlLink: e.html_link,
      location: e.location,
      description: e.description,
    })) : [];

    return [...supabaseEvents, ...gEvents];
  }, [termine, googleEvents, betriebFilter, showGoogleEvents]);

  const navigate = useCallback((dir: number) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  }, [currentDate, view]);

  const goToday = useCallback(() => setCurrentDate(new Date()), []);

  const getEventsForDate = useCallback((date: Date) => {
    return allEvents.filter(e => {
      const eDate = new Date(e.start);
      return isSameDay(eDate, date);
    });
  }, [allEvents]);

  const headerLabel = useMemo(() => {
    if (view === 'day') return currentDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (view === 'week') {
      const week = getWeekDates(currentDate);
      return `${week[0].toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })} – ${week[6].toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  }, [currentDate, view]);

  // ── Event Pill ──
  const EventPill = ({ event, compact = false }: { event: typeof allEvents[0]; compact?: boolean }) => (
    <div className={`text-[9px] font-medium rounded px-1.5 ${compact ? 'py-0.5 truncate' : 'py-1 mb-0.5'}`}
      style={{ backgroundColor: event.bgColor, color: event.color, borderLeft: compact ? 'none' : `2px solid ${event.color}` }}>
      <span className="flex items-center gap-0.5">
        {event.source === 'google' && (
          <svg className="w-2 h-2 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="12" r="10" />
          </svg>
        )}
        {compact ? (
          <>{formatTime(event.start)} {event.title?.substring(0, 10)}</>
        ) : (
          <>{formatTime(event.start)}{event.end ? ` – ${formatTime(event.end)}` : ''} · {event.title}</>
        )}
      </span>
    </div>
  );

  // ── Month View ──
  const renderMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = new Date(year, month, 1).getDay();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    const today = new Date();

    const cells: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = startOffset - 1; i >= 0; i--) {
      cells.push({ date: new Date(year, month, -i), isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
      cells.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return (
      <div>
        <div className="grid grid-cols-7 mb-1">
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
            <div key={d} className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-gray-50 rounded-xl overflow-hidden">
          {cells.map((cell, i) => {
            const dayEvents = getEventsForDate(cell.date);
            const isToday = isSameDay(cell.date, today);
            return (
              <button key={i}
                onClick={() => { setCurrentDate(cell.date); setView('day'); }}
                className={`min-h-[72px] p-1.5 text-left transition-all hover:bg-blue-50/50 relative ${
                  cell.isCurrentMonth ? 'bg-white' : 'bg-gray-50/50'
                } ${isToday ? 'ring-2 ring-inset ring-red-200' : ''}`}
              >
                <span className={`text-xs font-bold block mb-0.5 ${
                  isToday ? 'text-white bg-red-600 w-6 h-6 rounded-full flex items-center justify-center' :
                  cell.isCurrentMonth ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  {cell.date.getDate()}
                </span>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map((evt, j) => (
                    <EventPill key={j} event={evt} compact />
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[9px] font-bold text-gray-400 px-1">+{dayEvents.length - 3}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Current Time Line Hook ──
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getTimeLinePosition = (startHour: number, totalHours: number, rowHeight: number) => {
    const now = currentTime;
    const currentHour = now.getHours() + now.getMinutes() / 60;
    if (currentHour < startHour || currentHour > startHour + totalHours) return null;
    return ((currentHour - startHour) / totalHours) * (totalHours * rowHeight);
  };

  // ── Week View ──
  const renderWeek = () => {
    const weekDates = getWeekDates(currentDate);
    const today = new Date();
    // 30-minute slots: each hour = 2 rows of 30px = 60px per hour
    const SLOT_H = 30; // px per 30-min slot
    const HOUR_H = SLOT_H * 2; // 60px per hour
    const START_HOUR = 6;
    const TOTAL_HOURS = 14; // 6:00 – 20:00
    const TOTAL_PX = TOTAL_HOURS * HOUR_H;
    const isCurrentWeek = weekDates.some(d => isSameDay(d, today));
    const now = currentTime;
    const nowH = now.getHours() + now.getMinutes() / 60;
    const timeLinePos = isCurrentWeek && nowH >= START_HOUR && nowH <= START_HOUR + TOTAL_HOURS
      ? ((nowH - START_HOUR) / TOTAL_HOURS) * TOTAL_PX
      : null;

    // Build slot labels: every 30 min
    const slots = Array.from({ length: TOTAL_HOURS * 2 }, (_, i) => {
      const h = START_HOUR + Math.floor(i / 2);
      const m = i % 2 === 0 ? '00' : '30';
      return { label: m === '00' ? `${h}:00` : '', isHour: m === '00', top: i * SLOT_H };
    });

    const getEventStyle = (event: typeof allEvents[0]) => {
      const startDate = new Date(event.start);
      const endDate = event.end ? new Date(event.end) : new Date(startDate.getTime() + 60 * 60000);
      const startH = startDate.getHours() + startDate.getMinutes() / 60;
      const endH = endDate.getHours() + endDate.getMinutes() / 60;
      const top = Math.max(0, (startH - START_HOUR) / TOTAL_HOURS) * TOTAL_PX;
      const height = Math.max(24, ((endH - startH) / TOTAL_HOURS) * TOTAL_PX - 2);
      return { top, height };
    };

    return (
      <div className="overflow-x-auto">
        <div className="min-w-full sm:min-w-[600px]">
          {/* Day headers */}
          <div className="grid grid-cols-[52px_repeat(7,1fr)] border-b border-gray-100 bg-white sticky top-0 z-20">
            <div />
            {weekDates.map((d, i) => {
              const isToday = isSameDay(d, today);
              return (
                <button key={i} onClick={() => { setCurrentDate(d); setView('day'); }}
                  className={`text-center py-2 transition-colors hover:bg-gray-50 border-l border-gray-100 ${isToday ? 'bg-red-50/40' : ''}`}>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                    {d.toLocaleDateString('de-DE', { weekday: 'short' })}
                  </div>
                  <div className={`text-sm font-bold mt-0.5 mx-auto w-7 h-7 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-[#C41E3A] text-white' : 'text-gray-700'
                  }`}>
                    {d.getDate()}
                  </div>
                </button>
              );
            })}
          </div>
          {/* Time grid */}
          <div className="relative" style={{ height: TOTAL_PX }}>
            {/* Slot lines */}
            {slots.map((slot, i) => (
              <div key={i} className="absolute left-0 right-0 pointer-events-none"
                style={{ top: slot.top, height: SLOT_H }}>
                <div className={`absolute left-0 right-0 border-t ${
                  slot.isHour ? 'border-gray-150' : 'border-gray-50'
                }`} style={{ borderColor: slot.isHour ? '#e5e7eb' : '#f3f4f6' }} />
                {slot.label && (
                  <div className="absolute text-[9px] font-semibold text-gray-300 text-right"
                    style={{ left: 0, width: 44, paddingRight: 6, top: -7 }}>
                    {slot.label}
                  </div>
                )}
              </div>
            ))}
            {/* Day columns */}
            <div className="absolute inset-0 grid grid-cols-[52px_repeat(7,1fr)]">
              <div />
              {weekDates.map((d, di) => {
                const isToday = isSameDay(d, today);
                const dayEvents = getEventsForDate(d);
                return (
                  <div key={di} className={`relative border-l border-gray-100 ${
                    isToday ? 'bg-red-50/20' : ''
                  }`}>
                    {dayEvents.map((evt, ti) => {
                      const { top, height } = getEventStyle(evt);
                      const startDate = new Date(evt.start);
                      const endDate = evt.end ? new Date(evt.end) : null;
                      return (
                        <div key={ti}
                          className="absolute left-0.5 right-0.5 rounded-md px-1.5 py-1 overflow-hidden cursor-pointer hover:brightness-95 transition-all group"
                          style={{
                            top,
                            height,
                            backgroundColor: evt.bgColor,
                            borderLeft: `3px solid ${evt.color}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            zIndex: 5,
                          }}>
                          <div className="text-[9px] font-bold truncate" style={{ color: evt.color }}>
                            {startDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                            {endDate ? ` – ${endDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}` : ''}
                          </div>
                          {height > 28 && (
                            <div className="text-[9px] font-semibold truncate mt-0.5" style={{ color: evt.color, opacity: 0.85 }}>
                              {evt.title}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {/* Current Time Line - slim */}
            {timeLinePos !== null && (
              <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: timeLinePos }}>
                <div className="flex items-center">
                  <div className="w-[52px] flex justify-end pr-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />
                  </div>
                  <div className="flex-1 h-px bg-[#C41E3A]" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Day View ──
  const renderDay = () => {
    const dayEvents = getEventsForDate(currentDate);
    const today = new Date();
    const isToday = isSameDay(currentDate, today);
    const SLOT_H = 30;
    const HOUR_H = SLOT_H * 2;
    const START_HOUR = 6;
    const TOTAL_HOURS = 14;
    const TOTAL_PX = TOTAL_HOURS * HOUR_H;
    const now = currentTime;
    const nowH = now.getHours() + now.getMinutes() / 60;
    const timeLinePosDay = isToday && nowH >= START_HOUR && nowH <= START_HOUR + TOTAL_HOURS
      ? ((nowH - START_HOUR) / TOTAL_HOURS) * TOTAL_PX
      : null;

    const slots = Array.from({ length: TOTAL_HOURS * 2 }, (_, i) => {
      const h = START_HOUR + Math.floor(i / 2);
      const m = i % 2 === 0 ? '00' : '30';
      return { label: m === '00' ? `${h}:00` : '', isHour: m === '00', top: i * SLOT_H };
    });

    const getEventStyle = (event: typeof allEvents[0]) => {
      const startDate = new Date(event.start);
      const endDate = event.end ? new Date(event.end) : new Date(startDate.getTime() + 60 * 60000);
      const startH = startDate.getHours() + startDate.getMinutes() / 60;
      const endH = endDate.getHours() + endDate.getMinutes() / 60;
      const top = Math.max(0, (startH - START_HOUR) / TOTAL_HOURS) * TOTAL_PX;
      const height = Math.max(28, ((endH - startH) / TOTAL_HOURS) * TOTAL_PX - 2);
      return { top, height };
    };

    return (
      <div className="relative" style={{ height: TOTAL_PX }}>
        {/* Slot lines */}
        {slots.map((slot, i) => (
          <div key={i} className="absolute left-0 right-0 pointer-events-none"
            style={{ top: slot.top }}>
            <div className={`absolute left-0 right-0 border-t ${
              slot.isHour ? 'border-gray-200' : 'border-gray-50'
            }`} />
            {slot.label && (
              <div className="absolute text-[9px] font-semibold text-gray-300 text-right"
                style={{ left: 0, width: 44, paddingRight: 6, top: -7 }}>
                {slot.label}
              </div>
            )}
          </div>
        ))}
        {/* Events */}
        <div className="absolute inset-0" style={{ left: 52 }}>
          {dayEvents.map((evt, i) => {
            const { top, height } = getEventStyle(evt);
            const startDate = new Date(evt.start);
            const endDate = evt.end ? new Date(evt.end) : null;
            const durationMin = endDate ? Math.round((endDate.getTime() - startDate.getTime()) / 60000) : 60;
            return (
              <div key={i}
                className="absolute left-0 right-2 rounded-lg px-3 py-1.5 overflow-hidden cursor-pointer hover:brightness-95 transition-all"
                style={{
                  top,
                  height,
                  backgroundColor: evt.bgColor,
                  borderLeft: `3px solid ${evt.color}`,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  zIndex: 5,
                }}>
                <p className="text-xs font-bold truncate" style={{ color: evt.color }}>{evt.title}</p>
                <p className="text-[10px] text-gray-500">
                  {formatTime(evt.start)}{evt.end ? ` – ${formatTime(evt.end)}` : ''}
                  {durationMin > 0 && ` · ${durationMin} Min`}
                </p>
                {height > 56 && (evt as any).location && (
                  <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {(evt as any).location}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {/* Current Time Line - slim */}
        {timeLinePosDay !== null && (
          <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: timeLinePosDay }}>
            <div className="flex items-center">
              <div className="w-[52px] flex justify-end pr-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />
              </div>
              <div className="flex-1 h-px bg-[#C41E3A]" />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="w-11 h-11 sm:w-8 sm:h-8 rounded-lg border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors">
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          </button>
          <button onClick={goToday}
            className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-gray-100 hover:bg-gray-50 text-gray-600 transition-colors">
            Heute
          </button>
          <button onClick={() => navigate(1)} className="w-11 h-11 sm:w-8 sm:h-8 rounded-lg border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors">
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </button>
          <h4 className="text-sm font-bold text-gray-900 ml-2">{headerLabel}</h4>
        </div>
        <div className="flex items-center gap-2">
          {/* Google Events Toggle */}
          {googleEvents.length > 0 && (
            <button onClick={() => setShowGoogleEvents(!showGoogleEvents)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all border ${
                showGoogleEvents ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-gray-50 border-gray-100 text-gray-400'
              }`}>
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5" /></svg>
              Google
            </button>
          )}
          {/* New Event Button */}
          <button onClick={() => onNewEvent(currentDate)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold text-white transition-all hover:scale-105 active:scale-95"
            style={{ backgroundColor: KOOP_RED }}>
            <Plus className="w-3 h-3" /> Termin
          </button>
          {/* Filter */}
          <div className="relative">
            <button onClick={() => setShowFilter(!showFilter)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-gray-100 hover:bg-gray-50 text-gray-500 transition-colors">
              <Filter className="w-3 h-3" />
              {betriebFilter === 'alle' ? 'Alle' : BETRIEBE[betriebFilter as BetriebId]?.name || betriebFilter}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showFilter && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-gray-100 shadow-lg z-50 py-1 min-w-[160px]">
                <button onClick={() => { setBetriebFilter('alle'); setShowFilter(false); }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${betriebFilter === 'alle' ? 'font-bold text-red-600' : 'text-gray-600'}`}>
                  Alle Betriebe
                </button>
                {BETRIEB_LIST.filter(b => b.hasData).map(b => (
                  <button key={b.id} onClick={() => { setBetriebFilter(b.id); setShowFilter(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${betriebFilter === b.id ? 'font-bold text-red-600' : 'text-gray-600'}`}>
                    {b.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* View Switcher */}
          <div className="flex bg-gray-100/80 rounded-lg p-0.5">
            {([
              { id: 'day' as const, label: 'Tag' },
              { id: 'week' as const, label: 'Woche' },
              { id: 'month' as const, label: 'Monat' },
            ]).map(v => (
              <button key={v.id} onClick={() => setView(v.id)}
                className={`px-3 py-2 sm:py-1.5 min-h-[44px] sm:min-h-0 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all ${
                  view === v.id ? 'bg-white shadow-sm text-red-600' : 'text-gray-400 hover:text-gray-600'
                }`}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Calendar Content */}
      {view === 'month' && renderMonth()}
      {view === 'week' && renderWeek()}
      {view === 'day' && renderDay()}
    </div>
  );
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const { openAkte, openForderungsmanager } = useApp();
  const { profile, impersonateUserId, teamMembers } = useAuth();
  const impersonatedMember = impersonateUserId ? teamMembers.find(m => m.id === impersonateUserId) : null;
  const vorname = impersonatedMember ? impersonatedMember.vorname : (profile?.name?.split(' ')[0] || 'Andreas');

  // Finanzen-Sichtbarkeit: nur admin oder explizit freigeschaltetes Modul (Issue #303)
  // Muss vor dem forderungen-useEffect deklariert sein (Abhängigkeit im Dep-Array)
  const isAdmin = profile?.rolle === 'admin';
  const effectiveIsAdmin = impersonateUserId ? (impersonatedMember?.rolle === 'admin') : isAdmin;
  const canViewFinanzen = effectiveIsAdmin || (
    impersonateUserId
      ? impersonatedMember?.module_permissions?.finanzen === true
      : profile?.modulePermissions?.finanzen === true
  );
  const [, setLocation] = useLocation();
  const { data: aktenCounts } = useAktenCounts();
  const { data: recentAkten, loading: aktenLoading } = useAkten();
  const { data: aufgaben, loading: aufgabenLoading } = useAufgaben();
  const { data: termine, loading: termineLoading } = useTermine();
  const { data: mitarbeiter } = useMitarbeiter();

  // Dashboard tabs
  type DashboardTab = 'uebersicht' | 'fahrzeuge';
  const [dashboardTab, setDashboardTab] = useState<DashboardTab>('uebersicht');

  // Google Calendar
  const gcal = useGoogleCalendar();
  const [googleEvents, setGoogleEvents] = useState<GoogleCalendarEvent[]>([]);
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newEventDate, setNewEventDate] = useState<Date | undefined>();

  // Offene Forderungen Mietwagen (aus View forderungsakten_vollstaendig, Issue #277)
  const [forderungenMietwagen, setForderungenMietwagen] = useState<{ anzahl: number; summe: number }>({
    anzahl: 0,
    summe: 0,
  });
  useEffect(() => {
    if (!canViewFinanzen) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('forderungsakten_vollstaendig')
        .select('brutto_betrag')
        .eq('status', 'offen')
        .eq('business_unit', 'mietwagen');
      if (cancelled || error || !data) return;
      const summe = data.reduce((s: number, row: { brutto_betrag: number | string | null }) =>
        s + Number(row.brutto_betrag ?? 0), 0);
      setForderungenMietwagen({ anzahl: data.length, summe });
    })();
    return () => { cancelled = true; };
  }, [canViewFinanzen]);

  // Fetch Google Calendar events when connected
  useEffect(() => {
    if (gcal.isConnected) {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      gcal.fetchEvents(start.toISOString(), end.toISOString()).then(events => {
        setGoogleEvents(events);
      });
    }
  }, [gcal.isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNewEvent = useCallback((date?: Date) => {
    setNewEventDate(date);
    setNewEventOpen(true);
  }, []);

  const handleCreateEvent = useCallback(async (event: { summary: string; description: string; location: string; start: string; end: string }) => {
    try {
      if (gcal.isConnected) {
        // Create in Google Calendar
        const created = await gcal.createEvent({
          summary: event.summary,
          description: event.description,
          location: event.location,
          start: new Date(event.start).toISOString(),
          end: new Date(event.end).toISOString(),
        });
        if (created) {
          // Refresh events
          const now = new Date();
          const start = new Date(now.getFullYear(), now.getMonth(), 1);
          const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
          const events = await gcal.fetchEvents(start.toISOString(), end.toISOString());
          setGoogleEvents(events);
          const { toast } = await import('sonner');
          toast.success('Termin in Google Calendar erstellt');
        }
      } else {
        // Save locally (fallback)
        const localEvents = JSON.parse(localStorage.getItem('local_calendar_events') || '[]');
        localEvents.push({
          id: `local_${Date.now()}`,
          summary: event.summary,
          description: event.description,
          location: event.location,
          start: new Date(event.start).toISOString(),
          end: new Date(event.end).toISOString(),
          source: 'local',
        });
        localStorage.setItem('local_calendar_events', JSON.stringify(localEvents));
        const { toast } = await import('sonner');
        toast.success('Termin lokal gespeichert (Google Calendar nicht verbunden)');
      }
    } catch (err: any) {
      const { toast } = await import('sonner');
      toast.error(`Termin konnte nicht erstellt werden: ${err?.message || 'Unbekannter Fehler'}`);
    }
  }, [gcal]);

  // Effektiver User (Impersonation oder eigener)
  const effectiveUserId = impersonateUserId || profile?.id || '';

  // Aufgaben-Sichtbarkeit: Mitarbeiter sehen NUR eigene Aufgaben
  // Admin (Andreas Koop) sieht ALLE Aufgaben
  const sichtbareAufgaben = (aufgaben || []).filter(a => {
    if (effectiveIsAdmin) return true; // Admin sieht alles
    // Mitarbeiter: nur Aufgaben die mir zugewiesen sind oder die ich erstellt habe
    const istZugewiesen = a.zugewiesen_an === effectiveUserId || 
      (a as any).zugewiesen_an_liste?.includes(effectiveUserId);
    const istErsteller = (a as any).erstellt_von === effectiveUserId;
    return istZugewiesen || istErsteller;
  });

  const openAufgaben = sichtbareAufgaben.filter(a => a.status === 'offen' || a.status === 'in_bearbeitung');
  const recentFive = recentAkten?.slice(0, 6) || [];
  const totalAkten = Object.values(aktenCounts || {}).reduce((sum, c) => sum + (c || 0), 0);
  const todayTermine = (termine || []).filter(t => isSameDay(new Date(t.beginn), new Date()));

  const stats = [
    { label: 'Akten gesamt', value: totalAkten, icon: FolderOpen },
    { label: 'Offene Aufgaben', value: openAufgaben.length, icon: CheckSquare },
    { label: 'Termine heute', value: todayTermine.length + googleEvents.filter(e => isSameDay(new Date(e.start), new Date())).length, icon: CalendarDays },
    { label: 'Team aktiv', value: filterKiAgent(mitarbeiter || [], effectiveIsAdmin).filter(m => m.ist_aktiv).length, icon: Users },
  ];

  return (
    <div className="p-2 sm:p-4 lg:p-6 space-y-4 sm:space-y-5 max-w-[1600px] mx-auto pb-16">
      {/* Welcome */}
      <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Willkommen, {vorname}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Google Calendar Status ist jetzt im Header als kleiner Punkt */}

      {/* Dashboard Tabs */}
      <div className="flex bg-gray-100/80 rounded-xl p-0.5 w-fit">
        {([
          { id: 'uebersicht' as const, label: 'Übersicht' },
          { id: 'fahrzeuge' as const, label: 'Fahrzeuge', icon: Car },
        ]).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setDashboardTab(id)}
            data-testid={`dashboard-tab-${id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${
              dashboardTab === id
                ? 'bg-white shadow-sm text-red-600'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {label}
          </button>
        ))}
      </div>

      {/* ── Fahrzeuge Tab ───────────────────────────────────────────────────── */}
      {dashboardTab === 'fahrzeuge' && (
        <Widget title="Fahrzeug-Alerts" icon={Car} badge={undefined}>
          <FahrzeugAlertsListe />
        </Widget>
      )}

      {/* ── Übersicht Tab ───────────────────────────────────────────────────── */}
      {dashboardTab === 'uebersicht' && (<>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        {stats.map(({ label, value, icon: Icon }) => (
          <div key={label} className="koop-stat-card">
            <div className="koop-stat-icon">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="koop-stat-value">{value}</p>
              <p className="koop-stat-label">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Offene Forderungen Mietwagen — nur für Nutzer mit finanzen-Berechtigung (Issue #303) */}
      {canViewFinanzen && (
        <button
          data-testid="kachel-offene-forderungen-mietwagen"
          onClick={() => openForderungsmanager()}
          className="group w-full text-left bg-white rounded-2xl border border-gray-100 px-5 py-4 flex items-center gap-4 hover:shadow-md transition-all"
          style={{ boxShadow: SHADOW_SM }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(217,119,6,0.08)' }}>
            <Scale className="w-5 h-5" style={{ color: '#D97706' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Offene Forderungen Mietwagen
            </p>
            <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
              <span className="text-xl font-bold text-gray-900 tracking-tight">
                {forderungenMietwagen.anzahl}
              </span>
              <span className="text-xs font-medium text-gray-500">Vorgänge</span>
              <span className="text-sm font-bold text-gray-700 ml-1">
                {new Intl.NumberFormat('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                  maximumFractionDigits: 0,
                }).format(forderungenMietwagen.summe)}
              </span>
              <span className="text-[10px] text-gray-400">brutto</span>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-red-500 transition-colors flex-shrink-0" />
        </button>
      )}



      {/* Main Grid: Calendar (2/3) + Sidebar (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Calendar Widget — takes 2 columns */}
        <Widget className="lg:col-span-2" title="Kalender" icon={CalendarDays}
          badge={`${todayTermine.length + googleEvents.filter(e => isSameDay(new Date(e.start), new Date())).length} heute`}>
          {termineLoading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-300" />
            </div>
          ) : (
            <CalendarWidget
              termine={termine || []}
              googleEvents={googleEvents}
              onNewEvent={handleNewEvent}
            />
          )}
        </Widget>

        {/* Right Sidebar */}
        <div className="space-y-4">
          {/* Team / Anwesenheit */}
          <Widget title="Team" icon={Users} badge={`${filterKiAgent(mitarbeiter || [], effectiveIsAdmin).filter(m => m.ist_aktiv).length}/${filterKiAgent(mitarbeiter || [], effectiveIsAdmin).length}`}>
            <div className="space-y-2">
              {(mitarbeiter || []).length === 0 ? (
                <div className="py-4 text-center">
                  <Users className="w-6 h-6 text-gray-200 mx-auto mb-1" />
                  <p className="text-xs text-gray-400">Keine Mitarbeiter</p>
                </div>
              ) : (
                filterKiAgent(mitarbeiter || [], effectiveIsAdmin).map(member => (
                  <div key={member.id} className="flex items-center gap-3 py-1">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ backgroundColor: member.ist_aktiv ? KOOP_RED : '#d1d5db' }}>
                        {getInitials(member.vorname, member.nachname)}
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${member.ist_aktiv ? STATUS_DOT.aktiv : STATUS_DOT.inaktiv}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-gray-800 truncate">{member.vorname} {member.nachname}</p>
                      <p className="text-[10px] text-gray-400">{member.rolle === 'ki_agent' ? 'KI-Agent' : member.rolle}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Widget>

          {/* Verkaufs-Empfehlungen — steueroptimale Verkaufszeitpunkte */}
          <Widget title="💰 Verkaufs-Empfehlungen" icon={TrendingUp}>
            <VerkaufsEmpfehlungenWidget />
          </Widget>

          {/* Verpflegung — Mahlzeiten-Dokumentation */}
          <Widget title="Verpflegung" icon={UtensilsCrossed}>
            <VerpflegungWidget />
          </Widget>

          {/* Offene Aufgaben */}
          <Widget title="Offene Aufgaben" icon={CheckSquare} badge={openAufgaben.length}
            action={
              <button onClick={() => setLocation('/aufgaben')} className="text-[10px] font-bold text-red-600 hover:underline">
                Alle →
              </button>
            }>
            {aufgabenLoading ? (
              <div className="py-6 text-center text-sm text-gray-400">Laden...</div>
            ) : openAufgaben.length === 0 ? (
              <div className="py-6 text-center">
                <CheckSquare className="w-8 h-8 text-gray-100 mx-auto mb-2" />
                <p className="text-xs text-gray-400">Keine offenen Aufgaben</p>
              </div>
            ) : (
              <div className="space-y-2">
                {openAufgaben.slice(0, 5).map(aufgabe => {
                  const betrieb = BETRIEBE[aufgabe.business_unit as BetriebId];
                  return (
                    <div key={aufgabe.id} className="flex items-start gap-2.5 py-1.5 group cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
                      <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: betrieb?.color?.accent || '#d1d5db' }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-800 truncate">{aufgabe.titel}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                            {betrieb?.name || aufgabe.business_unit}
                          </span>
                          {aufgabe.faellig_am && (
                            <span className="text-[9px] text-gray-400 flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5" />
                              {new Date(aufgabe.faellig_am).toLocaleDateString('de-DE')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Widget>
        </div>
      </div>

      {/* Fahrzeuge-Widgets: Pool-Status + Fleet-KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <Widget title="Poolfahrzeug BMW iX3" icon={Car}>
          <PoolFahrzeugStatusWidget />
        </Widget>
        <Widget title="Flotte — KPIs" icon={Car}>
          <FleetKPIWidget />
        </Widget>
      </div>

      {/* Bottom: Aktuelle Akten (full width) */}
      <Widget title="Aktuelle Akten" icon={FileText} badge={recentFive.length}
        noPadding
        action={
          <button onClick={() => setLocation('/akten/gutachten')} className="text-[10px] font-bold text-red-600 hover:underline">
            Alle anzeigen →
          </button>
        }>
        {aktenLoading ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">Laden...</div>
        ) : recentFive.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <FolderOpen className="w-8 h-8 text-gray-100 mx-auto mb-2" />
            <p className="text-xs text-gray-400">Keine Akten vorhanden</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentFive.map((akte, idx) => {
              const betrieb = BETRIEBE[akte.business_unit as BetriebId];
              const kundenName = akte.kunde_name || akte.titel;
              return (
                <button key={akte.id}
                  onClick={() => {
                    openAkte({
                      id: akte.id, aktenNr: akte.akten_nummer, titel: akte.titel,
                      kundenName, businessUnit: akte.business_unit as BusinessUnit,
                    });
                    setLocation(`/akten/${akte.business_unit}/${akte.id}/uebersicht`);
                  }}
                  className={`w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors text-left ${idx % 2 === 1 ? 'bg-gray-50/40' : ''}`}
                  style={{ borderLeft: `3px solid ${betrieb?.color?.accent || '#e5e7eb'}` }}>
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono font-medium text-gray-400">{akte.akten_nummer}</span>
                      <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${PRIORITAET_COLORS[akte.prioritaet as keyof typeof PRIORITAET_COLORS] || ''}`}>
                        {akte.prioritaet}
                      </span>
                    </div>
                    <span className="text-sm font-medium mt-0.5 truncate text-gray-900">{kundenName}</span>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0 gap-1">
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${STATUS_COLORS[akte.status as keyof typeof STATUS_COLORS] || ''}`}>
                      {STATUS_LABELS[akte.status as keyof typeof STATUS_LABELS] || akte.status}
                    </span>
                    <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                      {betrieb?.name || akte.business_unit}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Widget>

      {/* New Event Dialog */}
      <NewEventDialog
        open={newEventOpen}
        onClose={() => setNewEventOpen(false)}
        onSubmit={handleCreateEvent}
        initialDate={newEventDate}
      />

      </>)}
    </div>
  );
}
